import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface AnchoredMenuProps {
  open: boolean;
  onClose: () => void;
  /** The trigger element the menu is anchored to. */
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  className?: string;
  /** `'dialog'` renders a non-modal popover (DLG-13): no menuitem focus, `role="dialog"`. Default `'menu'`. */
  role?: 'menu' | 'dialog';
  ariaLabel?: string;
  'data-testid'?: string;
}

const EDGE_GAP = 8;
const MENU_GAP = 4;

/**
 * Dropdown menu rendered in a portal on document.body with fixed positioning, so it is never clipped
 * by an `overflow-hidden` ancestor. Opens below the trigger and flips upward when it would run past the
 * viewport bottom (or the fixed mobile tab bar, measured from the DOM). Aligned to the trigger's inline end (RTL aware).
 */
export const AnchoredMenu: React.FC<AnchoredMenuProps> = ({
  open,
  onClose,
  anchorRef,
  children,
  className = '',
  role = 'menu',
  ariaLabel,
  'data-testid': testId,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    const menu = menuRef.current;
    if (!anchor || !menu) return;
    const rect = anchor.getBoundingClientRect();
    const mw = menu.offsetWidth;
    const mh = menu.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tabBar = document.querySelector('[data-testid="mobile-tabbar"]');
    const tabBarTop =
      tabBar && getComputedStyle(tabBar).display !== 'none'
        ? tabBar.getBoundingClientRect().top
        : vh;
    const bottomLimit = Math.min(vh, tabBarTop);

    const rtl = getComputedStyle(anchor).direction === 'rtl';
    let left = rtl ? rect.left : rect.right - mw;
    left = Math.max(EDGE_GAP, Math.min(left, vw - mw - EDGE_GAP));

    let top = rect.bottom + MENU_GAP;
    if (top + mh > bottomLimit - EDGE_GAP) {
      const above = rect.top - MENU_GAP - mh;
      top = above >= EDGE_GAP ? above : Math.max(EDGE_GAP, bottomLimit - EDGE_GAP - mh);
    }
    setPos({ top, left });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const items = () =>
      Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    if (role === 'dialog') {
      menuRef.current
        ?.querySelector<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
        ?.focus();
    } else {
      items()[0]?.focus();
    }

    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        anchorRef.current?.focus();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const list = items();
        if (!list.length) return;
        e.preventDefault();
        const i = list.indexOf(document.activeElement as HTMLElement);
        const next = e.key === 'ArrowDown' ? (i + 1) % list.length : (i - 1 + list.length) % list.length;
        list[next].focus();
      }
    };
    const onScroll = (e: Event) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t)) return;
      // Only a scroll that can move the anchor closes the menu. Unrelated scrollers (e.g. the hidden alphaTab viewport
      // scrolling itself after a render) must not dismiss it.
      if (t !== document && t !== (document.documentElement as Node) && anchorRef.current && !t.contains(anchorRef.current)) return;
      onClose();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onClose);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      role={role}
      aria-label={ariaLabel}
      aria-modal={role === 'dialog' ? false : undefined}
      data-testid={testId}
      style={{
        position: 'fixed',
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        visibility: pos ? 'visible' : 'hidden',
      }}
      className={`z-[60] ${className}`}
    >
      {children}
    </div>,
    document.body
  );
};

AnchoredMenu.displayName = 'AnchoredMenu';
